// =============================================
// src/hooks/useFirebase.js
// =============================================
// 🔥 React Hooks - Firebase data ko state mein lane ke liye

import { useState, useEffect, useCallback } from "react";
import { LeadsService, CallLogsService, TasksService, TeamService, AccountingService, DashboardService } from "../firebase/databaseService";
import { authStateListener } from "../firebase/authService";

// 🔥 Auth


export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log("🔄 useAuth hook initializing...");

    const unsubscribe = authStateListener((userData) => {
      console.log("🔄 useAuth - callback received:", userData ? `User: ${userData.email}, Role: ${userData.role}` : "null");
      setUser(userData);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  console.log("🔄 useAuth state:", { user: user?.email, role: user?.role, loading });

  return { user, loading };
};

// 🔥 Leads
export const useLeads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = LeadsService.subscribeToLeads((data) => {
      setLeads(data);
      setLoading(false);
    });
    return unsub;
  }, []);

  const addLead = useCallback((data) => LeadsService.addLead(data), []);
  const updateLead = useCallback((id, data) => LeadsService.updateLead(id, data), []);
  const deleteLead = useCallback((id) => LeadsService.deleteLead(id), []);

  return { leads, loading, addLead, updateLead, deleteLead };
};

// 🔥 Call Logs
export const useCallLogs = () => {
  const [callLogs, setCallLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = CallLogsService.subscribeToCallLogs((data) => {
      setCallLogs(data);
      setLoading(false);
    });
    return unsub;
  }, []);

  const addCallLog = useCallback((data) => CallLogsService.addCallLog(data), []);

  return { callLogs, loading, addCallLog };
};

// 🔥 Tasks
export const useTasks = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = TasksService.subscribeToTasks((data) => {
      setTasks(data);
      setLoading(false);
    });
    return unsub;
  }, []);

  const createTask = useCallback((data) => TasksService.createTask(data), []);
  const updateTaskStatus = useCallback((id, s) => TasksService.updateTaskStatus(id, s), []);
  const deleteTask = useCallback((id) => TasksService.deleteTask(id), []);

  return { tasks, loading, createTask, updateTaskStatus, deleteTask };
};

// 🔥 Dashboard Stats
export const useDashboardStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = DashboardService.subscribeToStats((data) => {
      setStats(data);
      setLoading(false);
    });
    return unsub;
  }, []);

  return { stats, loading };
};

// 🔥 Team
export const useTeam = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadTeam = useCallback(async () => {
    try {
      setLoading(true);
      const data = await TeamService.getAllTeamMembers();
      setTeamMembers(data);
    } catch (e) {
      console.error("Team:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTeam(); }, [loadTeam]);

  return { teamMembers, loading, refreshTeam: loadTeam };
};

// 🔥 Accounting
export const useAccounting = () => {
  const [invoices, setInvoices] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [inv, exp] = await Promise.all([
        AccountingService.getAllInvoices(),
        AccountingService.getAllExpenses()
      ]);
      setInvoices(inv);
      setExpenses(exp);
    } catch (e) {
      console.error("Accounting:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const createInvoice = useCallback(async (d) => {
    const result = await AccountingService.createInvoice(d);
    loadData();
    return result;
  }, [loadData]);

  const addExpense = useCallback(async (d) => {
    await AccountingService.addExpense(d);
    loadData();
  }, [loadData]);

  return { invoices, expenses, loading, createInvoice, addExpense, refresh: loadData };
};